import { useEffect } from 'react';
import type { ScreenId } from '../protocol/types';
import type { DisplayStore } from '../realtime/store';
import { clearState, loadState, saveState } from './stateCache';

/**
 * Tiene allineato lo store con la copia sul dispositivo:
 * - all'avvio ricarica l'ultimo stato salvato (finché il server non manda lo snapshot);
 * - mentre la connessione è viva salva i dati, al massimo una volta ogni `debounceMs`;
 * - se la chiave è revocata cancella tutto.
 */
export function usePersistedState(
  store: DisplayStore,
  screen: ScreenId,
  { debounceMs = 5_000 }: { debounceMs?: number } = {},
): void {
  useEffect(() => {
    let cancelled = false;
    let timer: number | undefined;

    void loadState(screen).then((saved) => {
      if (!cancelled && saved) store.hydrate(saved.data, saved.savedAt);
    });

    const unsubscribe = store.subscribe(() => {
      const { data, connection } = store.getState();
      if (connection.status === 'unauthorized') {
        window.clearTimeout(timer);
        void clearState(screen);
        return;
      }
      // Solo con dati freschi: lo stato ricaricato non va risalvato come se fosse nuovo.
      if (connection.status !== 'live' && connection.status !== 'polling') return;
      if (Object.keys(data).length === 0) return;
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        const s = store.getState();
        void saveState(screen, s.data, s.connection.lastMessageAt ?? Date.now());
      }, debounceMs);
    });

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      unsubscribe();
    };
  }, [store, screen, debounceMs]);
}
